import { Application, ApplicationAnswers, ApplicationSection } from '@bybeam/platform-types';
import useApplication from '@platform-ui-common/hooks/contexts/useApplication.js';
import { ValidationMode } from '@platform-ui-common/hooks/useForm/index.js';
import { generateErrors, isValid } from '@platform-ui-common/hooks/useForm/validate.js';
import useApplicationConfig from '@platform-ui/application/hooks/useApplicationConfig.js';
import { getApplicantSections } from '@platform-ui/utilities/application/config.js';
import { SpecialSectionKeys } from '@platform-ui/utilities/application/configs/standards/types.js';
import { getSectionFormInit } from '@platform-ui/utilities/application/section.js';
import { useMemo } from 'react';

interface ApplicationProgress {
  percentage: number;
  isComplete: boolean;
}

export const isSectionComplete = (
  section: ApplicationSection,
  answers: Partial<ApplicationAnswers>,
  application: Application,
): boolean => {
  if (!section?.questionGroups) {
    return true;
  }
  const [initialData, validationConfig] = getSectionFormInit(section, answers, application);
  const { errors } = generateErrors(
    { formData: initialData, touchedFields: {} },
    validationConfig,
    true,
    ValidationMode.OnSubmit,
  );
  return isValid(initialData, validationConfig, errors);
};

export default function useApplicationProgress(): ApplicationProgress {
  const config = useApplicationConfig();
  const sections = getApplicantSections(config);
  const application = useApplication();
  const progressSection = sections.filter((section) => section.key !== 'review');
  const completedScreens = progressSection.filter((section) =>
    isSectionComplete(section, application?.answers, application),
  );
  const percentage = completedScreens.length / progressSection.length;
  const isComplete = progressSection
    .filter(({ key }) => key !== SpecialSectionKeys.Review)
    .every((screen) => completedScreens.includes(screen));
  return useMemo(() => ({ percentage, isComplete }), [percentage, isComplete]);
}
